/**
 * Smoke, sparks, rings and the numbers that fly off a kill.
 *
 * None of this is physics; a particle that has to be solved against a wall
 * is a debris body, and those live in the world. These are drawn, moved by
 * hand and forgotten, out of a fixed pool so a chain of barrels cannot make
 * the collector run mid-explosion.
 */

export type ParticleKind = "chip" | "smoke" | "spark" | "ring" | "score" | "tread";

export type Particle = {
  live: boolean;
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Seconds lived, and the total it gets. */
  age: number;
  life: number;
  size: number;
  angle: number;
  spin: number;
  colour: string;
  text?: string;
};

const MAX = 420;

export class Particles {
  private pool: Particle[] = [];
  private next = 0;

  constructor() {
    for (let i = 0; i < MAX; i++) {
      this.pool.push({
        live: false,
        kind: "chip",
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        age: 0,
        life: 1,
        size: 0.2,
        angle: 0,
        spin: 0,
        colour: "#191919",
      });
    }
  }

  get all(): readonly Particle[] {
    return this.pool;
  }

  clear(): void {
    for (const p of this.pool) p.live = false;
  }

  /** The oldest slot is reused, so the newest effect always shows. */
  private take(): Particle {
    const p = this.pool[this.next] as Particle;
    this.next = (this.next + 1) % MAX;
    p.live = true;
    p.age = 0;
    p.text = undefined;
    p.spin = 0;
    p.angle = 0;
    return p;
  }

  spawn(kind: ParticleKind, x: number, y: number, opts: Partial<Particle> = {}): Particle {
    const p = this.take();
    p.kind = kind;
    p.x = x;
    p.y = y;
    p.vx = opts.vx ?? 0;
    p.vy = opts.vy ?? 0;
    p.life = opts.life ?? 0.6;
    p.size = opts.size ?? 0.22;
    p.colour = opts.colour ?? "#191919";
    p.angle = opts.angle ?? 0;
    p.spin = opts.spin ?? 0;
    p.text = opts.text;
    return p;
  }

  /** A handful of chips flying off something that just broke. */
  chips(x: number, y: number, colour: string, n = 6, speed = 7): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = speed * (0.4 + Math.random() * 0.9);
      this.spawn("chip", x, y, {
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v,
        life: 0.5 + Math.random() * 0.5,
        size: 0.1 + Math.random() * 0.22,
        angle: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 14,
        colour,
      });
    }
  }

  puff(x: number, y: number, n = 5, colour = "#3a3a34"): void {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      this.spawn("smoke", x, y, {
        vx: Math.cos(a) * 1.6,
        vy: Math.sin(a) * 1.6,
        life: 0.7 + Math.random() * 0.6,
        size: 0.35 + Math.random() * 0.5,
        colour,
      });
    }
  }

  sparks(x: number, y: number, dirX: number, dirY: number, n = 5): void {
    for (let i = 0; i < n; i++) {
      const spread = (Math.random() - 0.5) * 1.1;
      const cos = Math.cos(spread);
      const sin = Math.sin(spread);
      const v = 6 + Math.random() * 8;
      this.spawn("spark", x, y, {
        vx: (dirX * cos - dirY * sin) * v,
        vy: (dirX * sin + dirY * cos) * v,
        life: 0.18 + Math.random() * 0.16,
        size: 0.14,
        colour: "#ffed00",
      });
    }
  }

  ring(x: number, y: number, size: number, colour = "#ffd212", life = 0.42): void {
    this.spawn("ring", x, y, { life, size, colour });
  }

  score(x: number, y: number, text: string, colour = "#ffd212"): void {
    this.spawn("score", x, y, { vy: -2.4, life: 0.9, size: 0.9, colour, text });
  }

  /** A track mark. Long-lived, still, and drawn under everything. */
  tread(x: number, y: number, angle: number): void {
    this.spawn("tread", x, y, { life: 2.6, size: 0.34, angle, colour: "#191919" });
  }

  step(dt: number): void {
    for (const p of this.pool) {
      if (!p.live) continue;
      p.age += dt;
      if (p.age >= p.life) {
        p.live = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.angle += p.spin * dt;
      if (p.kind === "chip") {
        p.vx *= 0.94;
        p.vy *= 0.94;
      } else if (p.kind === "smoke") {
        p.vx *= 0.97;
        p.vy *= 0.97;
      } else if (p.kind === "spark") {
        p.vx *= 0.9;
        p.vy *= 0.9;
      } else if (p.kind === "score") {
        p.vy *= 0.95;
      }
    }
  }
}
